"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import type { LogQueryParams, ResponseStoredFilter, FixedStatusFilter, SortDirection, SortableField } from "@/types/filters";
import { SORTABLE_FIELDS } from "@/types/filters";
import { ERROR_CLASSIFICATIONS, type ErrorClassification } from "@/types/logs";
import { parseFlexibleDate, expandCollapsedInstantRange } from "@/utils/date-utils";
import { buildQueryString, type QueryParamValue } from "@/lib/api/api-client";

export type UrlFilterState = Omit<LogQueryParams, "page" | "pageSize" | "cursor"> & {
  /** Reserved for callers that previously opted out of a default lookback. */
  lookbackAll?: boolean;
};

const RESPONSE_STORED_VALUES: ResponseStoredFilter[] = ["all", "stored", "not-stored"];
const FIXED_STATUS_VALUES: FixedStatusFilter[] = ["all", "fixed", "not-fixed"];
const SORT_DIRECTIONS: SortDirection[] = ["asc", "desc"];

/** Empty filter set = Logs initial stage (no query params). */
export const EMPTY_URL_FILTERS: UrlFilterState = {};

function splitList(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

function parseStatusCodes(value: string | null): number[] | undefined {
  const codes = splitList(value)
    .map((v) => Number.parseInt(v, 10))
    .filter((n) => Number.isFinite(n) && n >= 100 && n <= 599);
  return codes.length > 0 ? codes : undefined;
}

function parseErrorStatuses(value: string | null): ErrorClassification[] | undefined {
  const values = splitList(value).filter((v): v is ErrorClassification =>
    (ERROR_CLASSIFICATIONS as readonly string[]).includes(v),
  );
  return values.length > 0 ? values : undefined;
}

function parseDate(value: string | null): Date | undefined {
  if (!value) return undefined;
  return parseFlexibleDate(value) ?? undefined;
}

function parseNonNegativeInt(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

/**
 * Parses URL search params into filter state on the client. Unlike the
 * server-side Zod validation in `validation/log-query-schema.ts`, this is
 * deliberately lenient: any value that fails to parse is silently dropped
 * rather than surfaced as an error, so a stale or hand-edited URL degrades
 * to "no filter" instead of breaking the page.
 *
 * No createdAt range is injected by default — dates only apply when the
 * user (or URL) sets them explicitly.
 */
export function parseFiltersFromSearchParams(searchParams: URLSearchParams): UrlFilterState {
  const userIds = splitList(searchParams.get("userId"));
  const routes = splitList(searchParams.get("route"));
  const methods = splitList(searchParams.get("method")).map((m) => m.toUpperCase());
  const responseStoredRaw = searchParams.get("responseStored");
  const fixedStatusRaw = searchParams.get("fixedStatus");
  const sortByRaw = searchParams.get("sortBy");
  const sortDirectionRaw = searchParams.get("sortDirection");
  const lookbackAll = searchParams.get("lookback") === "all";

  const created = expandCollapsedInstantRange(
    parseDate(searchParams.get("createdFrom")),
    parseDate(searchParams.get("createdTo")),
  );
  const updated = expandCollapsedInstantRange(
    parseDate(searchParams.get("updatedFrom")),
    parseDate(searchParams.get("updatedTo")),
  );

  return {
    userIds: userIds.length > 0 ? userIds : undefined,
    routes: routes.length > 0 ? routes : undefined,
    methods: methods.length > 0 ? methods : undefined,
    errorStatuses: parseErrorStatuses(searchParams.get("status")),
    statusCodes: parseStatusCodes(searchParams.get("statusCode")),
    responseStored: RESPONSE_STORED_VALUES.includes(responseStoredRaw as ResponseStoredFilter)
      ? (responseStoredRaw as ResponseStoredFilter)
      : undefined,
    fixedStatus: FIXED_STATUS_VALUES.includes(fixedStatusRaw as FixedStatusFilter)
      ? (fixedStatusRaw as FixedStatusFilter)
      : undefined,
    createdFrom: created.from,
    createdTo: created.to,
    updatedFrom: updated.from,
    updatedTo: updated.to,
    responseTimeMinMs: parseNonNegativeInt(searchParams.get("responseTimeMinMs")),
    responseTimeMaxMs: parseNonNegativeInt(searchParams.get("responseTimeMaxMs")),
    search: searchParams.get("search")?.trim() || undefined,
    sortBy: (SORTABLE_FIELDS as readonly string[]).includes(sortByRaw ?? "")
      ? (sortByRaw as SortableField)
      : undefined,
    sortDirection: SORT_DIRECTIONS.includes(sortDirectionRaw as SortDirection)
      ? (sortDirectionRaw as SortDirection)
      : undefined,
    lookbackAll: lookbackAll || undefined,
  };
}

export function serializeFilters(filters: UrlFilterState): string {
  const params: Record<string, QueryParamValue> = {
    userId: filters.userIds,
    route: filters.routes,
    method: filters.methods,
    status: filters.errorStatuses as string[] | undefined,
    statusCode: filters.statusCodes,
    responseStored: filters.responseStored,
    fixedStatus: filters.fixedStatus,
    createdFrom: filters.createdFrom?.toISOString(),
    createdTo: filters.createdTo?.toISOString(),
    updatedFrom: filters.updatedFrom?.toISOString(),
    updatedTo: filters.updatedTo?.toISOString(),
    responseTimeMinMs: filters.responseTimeMinMs,
    responseTimeMaxMs: filters.responseTimeMaxMs,
    search: filters.search,
    sortBy: filters.sortBy,
    sortDirection: filters.sortDirection,
    lookback: filters.lookbackAll ? "all" : undefined,
  };
  return buildQueryString(params);
}

export function countActiveFilters(filters: UrlFilterState): number {
  let count = 0;
  if (filters.userIds?.length) count += 1;
  if (filters.routes?.length) count += 1;
  if (filters.methods?.length) count += 1;
  if (filters.errorStatuses?.length) count += 1;
  if (filters.statusCodes?.length) count += 1;
  if (filters.responseStored && filters.responseStored !== "all") count += 1;
  if (filters.fixedStatus && filters.fixedStatus !== "all") count += 1;
  if (filters.createdFrom || filters.createdTo) count += 1;
  if (filters.updatedFrom || filters.updatedTo) count += 1;
  if (filters.responseTimeMinMs !== undefined || filters.responseTimeMaxMs !== undefined) count += 1;
  if (filters.search) count += 1;
  return count;
}

function filtersSignature(filters: UrlFilterState): string {
  return serializeFilters(filters);
}

export interface UseUrlFiltersResult {
  filters: UrlFilterState;
  setFilters: (patch: Partial<UrlFilterState>) => void;
  /** Update filter state without writing the address bar (e.g. dashboard default window). */
  setFiltersLocal: (patch: Partial<UrlFilterState>) => void;
  clearFilters: () => void;
  isPending: boolean;
}

/**
 * Sync filter state into the URL without a Next.js soft navigation.
 * `router.replace` would fire an `/_rsc` flight request and remount the
 * Suspense boundary; `history.replaceState` updates the address bar only.
 */
function replaceUrl(pathname: string, query: string): void {
  const next = `${pathname}${query}`;
  if (typeof window !== "undefined" && `${window.location.pathname}${window.location.search}` !== next) {
    window.history.replaceState(null, "", next);
  }
}

const UrlFiltersContext = createContext<UseUrlFiltersResult | null>(null);

/**
 * One shared filter store per page tree. LogFilters + LogsClient/DashboardClient
 * must share this — separate useState copies caused the chip to update while
 * the table kept fetching with stale/empty filters (or the reverse).
 */
export function UrlFiltersProvider({ children }: { children: ReactNode }) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const searchParamsKey = searchParams.toString();

  const [filters, setFiltersState] = useState<UrlFilterState>(() =>
    parseFiltersFromSearchParams(searchParams),
  );
  /** Skip one URL→state sync after we wrote the URL ourselves. */
  const skipNextUrlSyncRef = useRef<string | null>(null);
  /** Keep in-memory defaults (e.g. dashboard 2-day window) when the URL stays empty. */
  const preserveLocalOnEmptyUrlRef = useRef(false);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const fromUrl = parseFiltersFromSearchParams(searchParams);
    const signature = filtersSignature(fromUrl);
    if (skipNextUrlSyncRef.current === signature) {
      skipNextUrlSyncRef.current = null;
      return;
    }
    if (signature === "" && preserveLocalOnEmptyUrlRef.current) {
      return;
    }
    preserveLocalOnEmptyUrlRef.current = false;
    filtersRef.current = fromUrl;
    setFiltersState(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsKey]);

  const applyPatch = useCallback((prev: UrlFilterState, patch: Partial<UrlFilterState>): UrlFilterState => {
    const next: UrlFilterState = { ...prev, ...patch };
    if (patch.createdFrom !== undefined || patch.createdTo !== undefined) {
      if (patch.createdFrom || patch.createdTo) {
        next.lookbackAll = undefined;
      }
    }
    for (const key of Object.keys(patch) as Array<keyof UrlFilterState>) {
      if (patch[key] === undefined) {
        delete next[key];
      }
    }
    return next;
  }, []);

  const setFilters = useCallback(
    (patch: Partial<UrlFilterState>) => {
      const next = applyPatch(filtersRef.current, patch);
      // Never call history/router APIs inside a setState updater — React may
      // re-run updaters during render (Dashboard seed → Router update error).
      skipNextUrlSyncRef.current = filtersSignature(next);
      preserveLocalOnEmptyUrlRef.current = false;
      filtersRef.current = next;
      setFiltersState(next);
      replaceUrl(pathname, serializeFilters(next));
    },
    [pathname, applyPatch],
  );

  const setFiltersLocal = useCallback(
    (patch: Partial<UrlFilterState>) => {
      const next = applyPatch(filtersRef.current, patch);
      preserveLocalOnEmptyUrlRef.current = true;
      filtersRef.current = next;
      setFiltersState(next);
    },
    [applyPatch],
  );

  const clearFilters = useCallback(() => {
    skipNextUrlSyncRef.current = filtersSignature(EMPTY_URL_FILTERS);
    preserveLocalOnEmptyUrlRef.current = false;
    filtersRef.current = {};
    setFiltersState({});
    replaceUrl(pathname, "");
  }, [pathname]);

  const value = useMemo(
    () => ({ filters, setFilters, setFiltersLocal, clearFilters, isPending: false }),
    [filters, setFilters, setFiltersLocal, clearFilters],
  );

  return <UrlFiltersContext.Provider value={value}>{children}</UrlFiltersContext.Provider>;
}

export function useUrlFilters(): UseUrlFiltersResult {
  const ctx = useContext(UrlFiltersContext);
  if (!ctx) {
    throw new Error("useUrlFilters must be used within UrlFiltersProvider");
  }
  return ctx;
}
